import * as React from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';

export interface StoreCtx {
  /** Selected source DB name, e.g. "DALEEP_20260501". null = let the API pick the configured default. */
  selected: string | null;
  setSelected: (store: string | null) => void;
}

const Ctx = React.createContext<StoreCtx | null>(null);

export function StoreProvider({ children }: { children: React.ReactNode }) {
  const store = useRouterState({
    select: (s) => (s.location.search as { store?: string }).store ?? null,
  });
  const nav = useNavigate();

  const setSelected = React.useCallback((next: string | null) => {
    nav({
      to: '.',
      search: (prev) => {
        const merged: Record<string, unknown> = { ...(prev as Record<string, unknown>) };
        if (next) merged.store = next;
        else delete merged.store;
        return merged;
      },
      replace: true,
    });
  }, [nav]);

  const value = React.useMemo(
    () => ({ selected: store, setSelected }),
    [store, setSelected],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useStore(): StoreCtx {
  const v = React.useContext(Ctx);
  if (!v) throw new Error('StoreProvider missing');
  return v;
}
